import { useAuth0, User } from '@auth0/auth0-react'
import { isAfter, isValid, parseISO } from 'date-fns'
import React, { useMemo, useState } from 'react'

import { Dispensary } from '../../../../graphql/camel'
import useSiteMetadata from '../../../../hooks/useSiteMetadata'
import { useDispensaryByCodeEager } from '../../../../lib/dispensaries/hooks/useDispensaryByCodeEager'
import { AwsEventData, AwsEventKeys, setAwsEvent } from '../../../../lib/events/state'
import { usePractitionerFormsState } from '../../../../lib/onboarding/hooks/usePractitionerFormsState'
import { usePractitionerSettings } from '../../../../lib/practitioner-settings/hooks/usePractitionerSettings'
import { areCommissionsRelatedFormsNeeded } from '../../../../lib/practitioner-settings/utils'
import { userClaimIds } from '../../../../utils/userClaims'
import CommissionsModal from './CommissionsModal'
import CommissionsToast from './CommissionsToast'

export const publishEvent = ({
  eventKey,
  user,
  dispensary,
}: {
  eventKey: AwsEventKeys
  user?: User
  dispensary?: Dispensary
}): void => {
  const eventData: AwsEventData = {
    netSuiteId: user?.[userClaimIds.netSuiteId],
    auth0Id: user?.sub,
    email: user?.email,
    practitionerCode: user?.[userClaimIds.dispensaryPractitionerCode],
    dispensarySlug: dispensary?.slug,
    userAgent: window.navigator.userAgent,
    origin: window.location.origin,
    time: new Date(),
  }
  setAwsEvent({
    eventKey,
    ...eventData,
  })
}

const CommissionsNotification: React.FC = () => {
  const { commissionsNotificationUseModal } = useSiteMetadata() ?? {}
  const notificationType = commissionsNotificationUseModal ? 'modal' : 'toast'
  const [showModal, setShowModal] = useState(false)
  const [settings, { loading: settingsLoading }] = usePractitionerSettings()
  const practitionerFormsState = usePractitionerFormsState()
  const { user } = useAuth0()
  const { data: dispensary } = useDispensaryByCodeEager({
    practitionerCode: user?.[userClaimIds.dispensaryPractitionerCode],
  })

  const excludedPathPatterns = [
    /^\/products/,
    /^\/checkout/,
    /^\/onboarding/,
    /^\/account\/settings/,
    /^\/account\/dispensary\/dashboard/,
  ]

  /* Forms are needed if neither set of forms (commissions & commissions opt-out) has been submitted
   * and if the practitioner has not opted out of their dispensary */
  const practitionerFormsAreNeeded = useMemo(() => {
    const practitionerSettings = settings?.practitioner
    return practitionerSettings ? areCommissionsRelatedFormsNeeded(practitionerSettings) : false
  }, [settings])

  // If the notifyAfter date is in the past, the reminder is overdue & should be shown
  const reminderIsOverdue = useMemo<boolean>(() => {
    if (!practitionerFormsState) {
      return true
    }
    const { lastEvent, notifyAfter } = practitionerFormsState
    const notifyAfterDate = parseISO(notifyAfter)
    return lastEvent === 'prac_form_initialized' || !isValid(notifyAfterDate)
      ? true
      : isAfter(new Date(), notifyAfterDate)
  }, [practitionerFormsState])

  return (
    <>
      {(notificationType === 'modal' || showModal) && (
        <CommissionsModal
          practitionerSettings={settings?.practitioner}
          excludedPathPatterns={excludedPathPatterns}
          showModal={showModal}
          setShowModal={setShowModal}
          reminderIsOverdue={reminderIsOverdue}
          practitionerFormsAreNeeded={practitionerFormsAreNeeded}
          notificationType={notificationType}
          user={user}
          dispensary={dispensary ?? undefined}
        />
      )}

      {notificationType === 'toast' && (
        <CommissionsToast
          settingsLoading={settingsLoading}
          excludedPathPatterns={excludedPathPatterns}
          showModal={showModal}
          setShowModal={setShowModal}
          reminderIsOverdue={reminderIsOverdue}
          practitionerFormsAreNeeded={practitionerFormsAreNeeded}
          practitionerFormsState={practitionerFormsState}
          user={user}
          dispensary={dispensary ?? undefined}
        />
      )}
    </>
  )
}

export default CommissionsNotification
